import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Phone, MapPin, MessageCircle, Send, Loader2,
} from "lucide-react";
import SectionHeading from "./SectionHeading";
import { useI18n } from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_NUMBER = "971542005570";
const CONTACT_EMAIL = "info@primezone.ae";
const CONTACT_PHONE = "+971 54 200 5570";
const MAP_PLACE_URL = "https://maps.app.goo.gl/pcVPWwA6fK2gQrja7";
const MAP_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3608.3364225721302!2d55.37115507538394!3d25.25926597767096!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjXCsDE1JzMzLjQiTiA1NcKwMjInMjUuNCJF!5e0!3m2!1sen!2sae!4v1782305857449!5m2!1sen!2sae";

const FIELDS = [
  { name: "full_name", type: "text", required: true },
  { name: "company_name", type: "text" },
  { name: "country", type: "text" },
  { name: "phone", type: "tel", required: true },
  { name: "email", type: "email", required: true },
  { name: "product_requirement", type: "text" },
  { name: "brand_model", type: "text" },
  { name: "quantity", type: "text" },
];

const INITIAL = FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: "" }), { message: "" });

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.full_name || !form.email || !form.phone) {
      toast.error(t("contact.toast_validation"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v?.trim?.() || v])
      );
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await axios.post(`${API}/inquiries`, payload);
      toast.success(t("contact.toast_success"));
      setForm(INITIAL);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        t("contact.toast_error");
      toast.error(typeof msg === "string" ? msg : t("contact.toast_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 noise">
      <div className="glow-blue" style={{ width: 560, height: 560, left: -180, top: 0, background: "radial-gradient(circle at center, rgba(22,75,255,0.3), transparent 60%)" }} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <SectionHeading
          testId="contact-heading"
          eyebrow={t("contact.eyebrow")}
          title={<>
            {t("contact.title_a")}{" "}
            <span className="text-gradient-brand">{t("contact.title_b")}</span>
            {t("contact.title_dot")}
          </>}
          subtitle={t("contact.subtitle")}
        />

        <div className="mt-12 grid lg:grid-cols-12 gap-6 md:gap-8">
          <aside className="lg:col-span-4 reveal">
            <div className="glass-card rounded-3xl p-7 md:p-8 h-full">
              <h3 className="font-display text-[18px] font-semibold text-white">
                {t("contact.company_block.name")}
              </h3>
              <p className="mt-1 text-[13.5px] text-[#A8A8B3]">
                {t("contact.company_block.tagline")}
              </p>

              <div className="hairline my-6" />

              <ul className="space-y-5">
                <InfoRow icon={MapPin} label={t("contact.labels.location")}>
                  <a
                    href={MAP_PLACE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-info-location"
                    className="block text-[14px] text-white mt-0.5 leading-snug whitespace-pre-line hover:text-[#C9D0FF] transition-colors group"
                  >
                    {t("contact.address")}
                    <span className="block mt-1 text-[11.5px] text-[#8B6DFF] group-hover:text-[#A78BFF]">
                      {t("contact.directions")}
                    </span>
                  </a>
                </InfoRow>
                <InfoRow icon={Phone} label={t("contact.labels.phone")}>
                  <a href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`} data-testid="contact-info-phone" className="text-[14px] text-white mt-0.5 hover:underline" dir="ltr">
                    {CONTACT_PHONE}
                  </a>
                </InfoRow>
                <InfoRow icon={Mail} label={t("contact.labels.email")}>
                  <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-info-email" className="text-[14px] text-white mt-0.5 hover:underline" dir="ltr">
                    {CONTACT_EMAIL}
                  </a>
                </InfoRow>
              </ul>

              <a data-testid="contact-whatsapp-cta" href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="btn-ghost w-full mt-8 justify-center">
                <MessageCircle className="w-4 h-4" />
                {t("common.whatsapp")}
              </a>
            </div>
          </aside>

          <form data-testid="contact-form" onSubmit={onSubmit} noValidate className="lg:col-span-8 reveal">
            <div className="glass-card rounded-3xl p-7 md:p-9">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {FIELDS.map((f) => (
                  <Field
                    key={f.name}
                    name={f.name}
                    label={t(`contact.fields.${f.name}`)}
                    type={f.type}
                    required={f.required}
                    value={form[f.name]}
                    onChange={(v) => setField(f.name, v)}
                  />
                ))}
                <div className="md:col-span-2">
                  <Label htmlFor="message">{t("contact.fields.message")}</Label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    data-testid="contact-field-message"
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    placeholder={t("contact.fields.message_placeholder")}
                    className="glass-input w-full rounded-xl px-4 py-3 text-[14.5px] resize-none"
                  />
                </div>
              </div>

              <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
                <p className="text-[12px] text-[#6F7280] max-w-md">
                  {t("contact.consent")}
                </p>
                <button data-testid="contact-submit" type="submit" disabled={submitting} className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("contact.submitting")}
                    </>
                  ) : (
                    <>
                      {t("contact.submit")} <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Map */}
        <div className="mt-12 reveal" data-testid="contact-map-wrapper">
          <div className="glass-card rounded-3xl p-2 md:p-3 overflow-hidden">
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <iframe
                title="Prime Zone FZCO — Dubai location"
                data-testid="contact-map-iframe"
                src={MAP_EMBED_URL}
                width="100%"
                height="420"
                style={{
                  border: 0,
                  filter: "invert(0.92) hue-rotate(180deg) saturate(0.85) brightness(0.95) contrast(0.95)",
                  display: "block",
                }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
              <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(60% 80% at 50% 100%, rgba(22,75,255,0.18), transparent 60%), radial-gradient(40% 60% at 50% 0%, rgba(122,92,255,0.14), transparent 70%)" }} />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1">
            <p className="text-[12.5px] text-[#A8A8B3]">{t("contact.map_caption")}</p>
            <a
              data-testid="contact-map-directions"
              href={MAP_PLACE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12.5px] text-[#C9CAD1] hover:text-white transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-white/60"
            >
              {t("contact.directions")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <li className="flex gap-3">
      <div className="icon-bubble shrink-0"><Icon className="w-4 h-4" /></div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#8B6DFF]">{label}</div>
        {children}
      </div>
    </li>
  );
}

function Label({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block text-[11px] tracking-[0.2em] uppercase text-[#A8A8B3] mb-1.5">
      {children}
    </label>
  );
}

function Field({ name, label, type, required, value, onChange }) {
  return (
    <div>
      <Label htmlFor={name}>
        {label}
        {required ? <span className="text-[#7A5CFF]"> *</span> : null}
      </Label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        data-testid={`contact-field-${name}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="glass-input w-full rounded-xl px-4 py-3 text-[14.5px]"
        placeholder={label}
        autoComplete="off"
      />
    </div>
  );
}
